import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { listScreenGroups, addGroupField, updateGroupField, removeGroupField } from "../../../api/t2tVendorScreenGroupApi";
import { useToast, useConfirm } from "../Layout/T2TUIProvider";

const FIELD_TYPES = ["text", "select", "textarea", "email", "tel", "number", "date"];

const GROUP_ICONS = {
  initial:    "🏷️",
  general:    "📍",
  tax:        "🧾",
  finance:    "🏦",
  purchasing: "🏭",
  partner:    "🤝",
};

// ── Field modal (add or edit) ─────────────────────────────────────────────────
const FieldModal = ({ groupCode, existing, onSaved, onClose }) => {
  const toast   = useToast();
  const isEdit  = !!existing;
  const [form, setForm] = useState(
    existing
      ? { field_key: existing.field_key, field_label: existing.field_label, field_type: existing.field_type, is_required: !!existing.is_required, sort_order: existing.sort_order }
      : { field_key: "", field_label: "", field_type: "text", is_required: false, sort_order: 99 }
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.field_key.trim() || !form.field_label.trim()) {
      toast("Field key and label are required.", "error"); return;
    }
    setSaving(true);
    const resp = isEdit
      ? await updateGroupField(groupCode, existing.field_id, {
          field_label: form.field_label,
          field_type:  form.field_type,
          is_required: form.is_required,
          sort_order:  form.sort_order,
        })
      : await addGroupField(groupCode, form);
    setSaving(false);
    if (resp.error) { toast(resp.error.message || "Failed to save field.", "error"); return; }
    toast(isEdit ? "Field updated." : "Field added.", "success");
    onSaved();
  };

  return (
    <div className="t2t-overlay" onClick={onClose}>
      <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="t2t-modal-head">
          <div className="t2t-modal-title">{isEdit ? "Edit Field" : "Add Field"}</div>
          <button className="t2t-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="t2t-modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="t2t-fg">
            <label>Field Key {!isEdit && <span style={{ color: "#DC2626" }}>*</span>}</label>
            <input
              className="t2t-fc"
              placeholder="e.g. pan_number"
              value={form.field_key}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, field_key: e.target.value.toLowerCase().replace(/\s/g, "_") })}
              style={isEdit ? { background: "#F8FAFC", color: "#94A3B8" } : {}}
            />
            {!isEdit && <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>Unique identifier, snake_case</div>}
          </div>
          <div className="t2t-fg">
            <label>Field Label <span style={{ color: "#DC2626" }}>*</span></label>
            <input className="t2t-fc" placeholder="e.g. PAN Number" value={form.field_label}
              onChange={(e) => setForm({ ...form, field_label: e.target.value })} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="t2t-fg">
              <label>Field Type</label>
              <select className="t2t-fc" value={form.field_type}
                onChange={(e) => setForm({ ...form, field_type: e.target.value })}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="t2t-fg">
              <label>Sort Order</label>
              <input className="t2t-fc" type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_required}
              onChange={(e) => setForm({ ...form, is_required: e.target.checked })} />
            Required field
          </label>
        </div>
        <div className="t2t-modal-foot">
          <button className="t2t-btn t2t-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="t2t-btn t2t-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Field"}
          </button>
        </div>
      </div>
    </div>
  );
};

FieldModal.propTypes = {
  groupCode: PropTypes.string.isRequired,
  existing:  PropTypes.object,
  onSaved:   PropTypes.func.isRequired,
  onClose:   PropTypes.func.isRequired,
};
FieldModal.defaultProps = { existing: null };

// ── Main component ────────────────────────────────────────────────────────────
const ScreenGroupsAdmin = () => {
  const toast   = useToast();
  const confirm = useConfirm();
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});
  const [modal,    setModal]    = useState(null); // { groupCode, existing? }

  const load = async () => {
    setLoading(true);
    const resp = await listScreenGroups();
    if (!resp.error) setGroups(resp.data?.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = (code) => setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));

  // Move field up/down within its group by swapping sort_order values
  const handleMove = async (groupCode, fields, fieldId, dir) => {
    const active = fields.filter((f) => f.is_active).sort((a, b) => a.sort_order - b.sort_order || a.field_id - b.field_id);
    const idx    = active.findIndex((f) => f.field_id === fieldId);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= active.length) return;

    const a = active[idx];
    const b = active[swapIdx];
    // Swap sort_order values
    await Promise.all([
      updateGroupField(groupCode, a.field_id, { sort_order: b.sort_order }),
      updateGroupField(groupCode, b.field_id, { sort_order: a.sort_order }),
    ]);
    load();
  };

  const handleDeactivate = async (groupCode, fieldId, fieldLabel) => {
    const ok = await confirm(`Deactivate field "${fieldLabel}"? It will no longer appear in the form.`, "Deactivate Field?");
    if (!ok) return;
    const resp = await removeGroupField(groupCode, fieldId);
    if (resp.error) { toast(resp.error.message || "Failed.", "error"); return; }
    toast("Field deactivated.", "success");
    load();
  };

  const handleReactivate = async (groupCode, fieldId, fieldLabel) => {
    const resp = await updateGroupField(groupCode, fieldId, { is_active: true });
    if (resp.error) { toast(resp.error.message || "Failed.", "error"); return; }
    toast(`"${fieldLabel}" re-activated.`, "success");
    load();
  };

  if (loading) return <div style={{ padding: 32, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading screen groups…</div>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Vendor Screen Groups</div>
        <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>
          6 SAP-aligned view containers defining which fields appear in each tab of the Vendor Creation form.
          Use ↑↓ to reorder fields, ✎ to edit, and the toggle to activate/deactivate.
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {groups.map((g) => {
          const isOpen      = expanded[g.code];
          const activeCount = (g.fields || []).filter((f) => f.is_active).length;
          const activeFields = (g.fields || []).filter((f) => f.is_active).sort((a, b) => a.sort_order - b.sort_order || a.field_id - b.field_id);
          const inactiveFields = (g.fields || []).filter((f) => !f.is_active);

          return (
            <div key={g.code} style={{ border: "1px solid var(--t2t-border)", borderRadius: 10, overflow: "hidden" }}>
              {/* Group header */}
              <div
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", cursor: "pointer", background: isOpen ? "#F8FAFC" : "#fff" }}
                onClick={() => toggle(g.code)}
              >
                <span style={{ fontSize: 20 }}>{GROUP_ICONS[g.code] || "📋"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{g.name}</div>
                  <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                    <span style={{ fontFamily: "monospace", background: "#F1F5F9", padding: "1px 6px", borderRadius: 4 }}>{g.code}</span>
                    <span style={{ marginLeft: 8 }}>{activeCount} active field{activeCount !== 1 ? "s" : ""}</span>
                    {inactiveFields.length > 0 && <span style={{ marginLeft: 6, color: "#94A3B8" }}>· {inactiveFields.length} inactive</span>}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginRight: 4 }}>Sort {g.sort_order}</span>
                <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}></i>
              </div>

              {/* Field table */}
              {isOpen && (
                <div style={{ borderTop: "1px solid var(--t2t-border)", padding: "10px 16px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#F8FAFC" }}>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748B", width: 50 }}>Order</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748B" }}>Field Key</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748B" }}>Label</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 600, color: "#64748B" }}>Type</th>
                        <th style={{ padding: "6px 8px", textAlign: "center", fontWeight: 600, color: "#64748B" }}>Req</th>
                        <th style={{ padding: "6px 8px", textAlign: "center", fontWeight: 600, color: "#64748B" }}>Status</th>
                        <th style={{ padding: "6px 8px", width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Active fields with move controls */}
                      {activeFields.map((f, idx) => (
                        <tr key={f.field_id} style={{ borderBottom: "1px solid #F1F5F9" }}>
                          <td style={{ padding: "7px 8px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <button
                                onClick={() => handleMove(g.code, g.fields, f.field_id, -1)}
                                disabled={idx === 0}
                                style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", color: idx === 0 ? "#E2E8F0" : "#64748B", fontSize: 10, padding: "1px 3px", lineHeight: 1 }}
                                title="Move up"
                              >▲</button>
                              <button
                                onClick={() => handleMove(g.code, g.fields, f.field_id, 1)}
                                disabled={idx === activeFields.length - 1}
                                style={{ background: "none", border: "none", cursor: idx === activeFields.length - 1 ? "default" : "pointer", color: idx === activeFields.length - 1 ? "#E2E8F0" : "#64748B", fontSize: 10, padding: "1px 3px", lineHeight: 1 }}
                                title="Move down"
                              >▼</button>
                            </div>
                          </td>
                          <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "var(--t2t-primary)" }}>{f.field_key}</td>
                          <td style={{ padding: "7px 8px", fontWeight: 500 }}>{f.field_label}</td>
                          <td style={{ padding: "7px 8px" }}>
                            <span style={{ fontSize: 10, background: "#EFF6FF", color: "#1D4ED8", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>{f.field_type}</span>
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>
                            {f.is_required ? <span style={{ color: "#DC2626", fontWeight: 700 }}>✓</span> : <span style={{ color: "#CBD5E1" }}>—</span>}
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}>Active</span>
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "right" }}>
                            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                              <button
                                onClick={() => setModal({ groupCode: g.code, existing: f })}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--t2t-primary)", fontSize: 13, padding: "2px 4px" }}
                                title="Edit field"
                              ><i className="bi bi-pencil"></i></button>
                              <button
                                onClick={() => handleDeactivate(g.code, f.field_id, f.field_label)}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 13, padding: "2px 4px" }}
                                title="Deactivate"
                              ><i className="bi bi-dash-circle"></i></button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Inactive fields (collapsed section) */}
                      {inactiveFields.map((f) => (
                        <tr key={f.field_id} style={{ borderBottom: "1px solid #F1F5F9", opacity: 0.45 }}>
                          <td style={{ padding: "7px 8px", color: "#94A3B8", fontSize: 10 }}>—</td>
                          <td style={{ padding: "7px 8px", fontFamily: "monospace", color: "#94A3B8" }}>{f.field_key}</td>
                          <td style={{ padding: "7px 8px", color: "#94A3B8" }}>{f.field_label}</td>
                          <td style={{ padding: "7px 8px" }}>
                            <span style={{ fontSize: 10, background: "#F8FAFC", color: "#94A3B8", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>{f.field_type}</span>
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "center", color: "#CBD5E1" }}>—</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#F8FAFC", color: "#94A3B8", border: "1px solid #E2E8F0" }}>Inactive</span>
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "right" }}>
                            <button
                              onClick={() => handleReactivate(g.code, f.field_id, f.field_label)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#16A34A", fontSize: 13, padding: "2px 4px" }}
                              title="Re-activate"
                            ><i className="bi bi-plus-circle"></i></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <button
                    className="t2t-btn t2t-btn-outline t2t-btn-sm"
                    style={{ marginTop: 10 }}
                    onClick={() => setModal({ groupCode: g.code, existing: null })}
                  >
                    + Add Field
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modal && (
        <FieldModal
          groupCode={modal.groupCode}
          existing={modal.existing}
          onSaved={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default ScreenGroupsAdmin;
